import { create } from 'zustand';
import type { Model3D, Tab, UserModel } from '@visionar/shared';

type AuthView = 'login' | 'register' | null;

// Union type for AR-viewable models
type ARModel = Model3D | UserModel;

interface UIState {
  // Navigation
  currentTab: Tab;
  setCurrentTab: (tab: Tab) => void;

  // Auth views
  authView: AuthView;
  setAuthView: (view: AuthView) => void;

  // Model detail (catalog models)
  selectedModel: Model3D | null;
  setSelectedModel: (model: Model3D | null) => void;

  // User model detail (uploaded models)
  selectedUserModel: UserModel | null;
  setSelectedUserModel: (model: UserModel | null) => void;

  // Upload drawer
  isUploadDrawerOpen: boolean;
  setUploadDrawerOpen: (open: boolean) => void;

  // AR
  isARActive: boolean;
  setARActive: (active: boolean) => void;
  arModel: ARModel | null;
  setARModel: (model: ARModel | null) => void;

  // VR
  isVRActive: boolean;
  setVRActive: (active: boolean) => void;

  // Toast notifications
  toast: { message: string; type: 'success' | 'error' | 'info' } | null;
  showToast: (message: string, type?: 'success' | 'error' | 'info') => void;
  hideToast: () => void;

  // Loading overlay
  isGlobalLoading: boolean;
  setGlobalLoading: (loading: boolean) => void;
}

export const useUIStore = create<UIState>((set) => ({
  // Navigation
  currentTab: 'home',
  setCurrentTab: (tab) => set({ currentTab: tab }),

  // Auth views
  authView: null,
  setAuthView: (view) => set({ authView: view }),

  // Model detail (catalog)
  selectedModel: null,
  setSelectedModel: (model) => set({ selectedModel: model }),

  // User model detail (uploaded)
  selectedUserModel: null,
  setSelectedUserModel: (model) => set({ selectedUserModel: model }),

  // Upload drawer
  isUploadDrawerOpen: false,
  setUploadDrawerOpen: (open) => set({ isUploadDrawerOpen: open }),

  // AR
  isARActive: false,
  setARActive: (active) => set({ isARActive: active }),
  arModel: null,
  setARModel: (model) => set({ arModel: model }),

  // VR
  isVRActive: false,
  setVRActive: (active) => set({ isVRActive: active }),

  // Toast
  toast: null,
  showToast: (message, type = 'info') => {
    set({ toast: { message, type } });
    // Auto-hide after 3 seconds
    setTimeout(() => {
      set((state) => (state.toast?.message === message ? { toast: null } : state));
    }, 3000);
  },
  hideToast: () => set({ toast: null }),

  // Loading
  isGlobalLoading: false,
  setGlobalLoading: (loading) => set({ isGlobalLoading: loading }),
}));
